<template>
  <!--
    PoolaRunHeader — the run's title + a single phase-appropriate countdown +
    the «بولشير» run-budget chip. A CONTENT FRAGMENT (no <f7-page>); lives at the
    top of PoolaRun.

    Countdown target (FE plan Section 1, clock-skew guard): the host's usePoolaDeal
    captures a server-time offset and the run feeds it down as `serverOffset`
    (serverNow − clientNow, ms). useCountdown measures `target − Date.now()`, so
    feeding it `targetMs − serverOffset` makes it count against SERVER time —
    algebraically `now = Date.now() + serverOffset`. The target is captured
    synchronously at setup; the deal is always loaded before PoolaRun mounts
    (the host only renders a run once `state` is coming/live/ended), so
    startAt/endAt are present here.
  -->
  <header
    class="poola-run-header"
    :data-phase="phase"
    :data-festival="festivalActive"
    :data-urgency="festivalUrgency"
  >
    <h2 class="poola-run-header__title">{{ title }}</h2>

    <!-- «موسم البولة» day counter — festival-only. Prefers the server's
         Cairo-calendar meta.festival.dayIndex/dayCount, falling back to the run's
         own startAt/endAt window when those BE fields are absent (open-ended run
         or a contract predating meta.festival). -->
    <p v-if="festivalActive && dayCounter" class="poola-run-header__day-counter">
      {{ t('poola.festival.dayCounter', { n: dayCounter.day, total: dayCounter.total }) }}
    </p>

    <div v-if="showCountdown" class="poola-run-header__countdown">
      <span class="poola-run-header__countdown-label">{{ countdownLabel }}</span>
      <!-- dir="ltr": days→seconds read left-to-right in every locale -->
      <div class="poola-run-header__cells" dir="ltr">
        <div class="poola-run-header__cell">
          <span class="poola-run-header__value">{{ pad(countdown.days) }}</span>
          <span class="poola-run-header__unit">{{ t('poola.countdown.days') }}</span>
        </div>
        <div class="poola-run-header__cell">
          <span class="poola-run-header__value">{{ pad(countdown.hours) }}</span>
          <span class="poola-run-header__unit">{{ t('poola.countdown.hours') }}</span>
        </div>
        <div class="poola-run-header__cell">
          <span class="poola-run-header__value">{{ pad(countdown.minutes) }}</span>
          <span class="poola-run-header__unit">{{ t('poola.countdown.minutes') }}</span>
        </div>
        <div class="poola-run-header__cell">
          <span class="poola-run-header__value">{{ pad(countdown.seconds) }}</span>
          <span class="poola-run-header__unit">{{ t('poola.countdown.seconds') }}</span>
        </div>
      </div>

      <!-- Escalating urgency badge — festival-only, real time-remaining thresholds. -->
      <span
        v-if="festivalActive && festivalUrgency !== 'none'"
        class="poola-run-header__urgency-badge"
      >{{ urgencyLabel }}</span>
    </div>

    <!-- Finale — last-hours window only: the "runs hot" skin (CSS, see :data-urgency)
         + the last-chance banner. -->
    <p v-if="festivalActive && festivalUrgency === 'lastHours'" class="poola-run-header__finale">
      {{ t('poola.festival.finale.lastChance') }}
    </p>

    <!-- Run-budget chip — the member's remaining «بولشير» budget for this run.
         Hidden when uncapped (myPoolshareRemainingCents === null) or ended. -->
    <span v-if="showBudget" class="poola-run-header__budget">
      {{ t('poola.buy.live.budgetRemaining', { n: formatCurrency(budgetCents) }) }}
    </span>

    <!-- Live-surface slots for P2-B (collective thermometer, savings line, live
         pill). Render nothing until fed — no BE festival data exists yet. -->
    <div v-if="festivalActive && hasLiveSurfaces" class="poola-run-header__live-surfaces">
      <slot name="thermometer" />
      <slot name="savings" />
      <slot name="live-pill" />
    </div>
  </header>
</template>

<script setup>
import { computed, useSlots } from 'vue';
import { useI18n } from 'vue-i18n';
import { useCountdown } from '@/composables/useCountdown.js';
import { useCurrency } from '@/composables/useCurrency.js';
import { computeFestivalDayCounter, computeFestivalUrgency } from '@/utils/poolaFestival.js';

const props = defineProps({
  phase: {
    type: String,
    required: true,
    validator: (v) => ['scheduled', 'active', 'ended'].includes(v),
  },
  // The deserialised poola-deals resource (startAt / endAt / status /
  // myPoolshareRemainingCents flattened onto it).
  deal: {
    type: Object,
    default: null,
  },
  // serverNow − clientNow (ms), captured once by usePoolaDeal. 0 = assume
  // in-sync (the safe default).
  serverOffset: {
    type: Number,
    default: 0,
  },
  // The member's live spendable «بولشير» wallet balance (cents), fed from the
  // run's useWallets()/claim meta.balances read. Used ONLY to clamp the budget
  // chip: `myPoolshareRemainingCents` is the RUN cap headroom (cap − used) and
  // ignores the wallet, so a member capped at 2000 but holding 300 would see
  // «باقي من ميزانيتك 2,000». The real spendable is min(cap headroom, balance) —
  // exactly what PoolaClaimSheet's maxQty already enforces. null = balance not
  // loaded yet → fall back to the raw cap headroom (no worse than before).
  poolshareBalanceCents: {
    type: Number,
    default: null,
  },
  // Suppress the personal «بولشير» run-budget chip entirely. Read-only viewers
  // (prospect) must never see a members-only PoolShare budget affordance even if
  // the backend returns a non-null myPoolshareRemainingCents (the prospect
  // contract allows "cap or null"). Defense-in-depth; default false keeps the
  // seller/supplier paths byte-for-byte unchanged.
  hideBudget: {
    type: Boolean,
    default: false,
  },
  // «موسم البولة» festival skin — a time-boxed treatment the caller opts into
  // for a live run window (FE plan: "festival = a SKIN toggled on for a live
  // run window"). Static/derived-only in P2-A: no new BE data, no realtime.
  festival: {
    type: Boolean,
    default: false,
  },
  // meta.festival aggregates from usePoolaDeal — used here ONLY for the
  // server-authoritative dayIndex/dayCount (Cairo-calendar, clamped; delivered
  // 2026-07-10 realtime-broadcast). null / missing fields → the day counter
  // falls back to the run's own startAt/endAt window. The thermometer/savings/
  // live-pill live surfaces are fed via slots by the run, not consumed here.
  festivalMeta: {
    type: Object,
    default: null,
  },
});

const { t } = useI18n({ useScope: 'global' });
const { formatCurrency } = useCurrency();

/** ISO string | epoch-ms | null → epoch ms | null (NaN-safe). */
function toMs(value) {
  if (value == null) return null;
  const ms = typeof value === 'number' ? value : Date.parse(value);
  return Number.isFinite(ms) ? ms : null;
}

// Offset-adjusted countdown target — captured ONCE at setup (a one-time read,
// deliberately not reactive: useCountdown wants a fixed target at its
// synchronous call site, and the deal is already loaded + the phase is stable
// for this instance's lifetime — the host remounts via :key on a status flip).
// scheduled counts to startAt; active counts to endAt; ended has no countdown.
function captureTargetMs() {
  let raw = null;
  if (props.phase === 'scheduled') raw = toMs(props.deal?.startAt);
  else if (props.phase === 'active') raw = toMs(props.deal?.endAt);
  return raw == null ? null : raw - props.serverOffset;
}
const adjustedTargetMs = captureTargetMs();

// useCountdown wants a concrete target. With no target (ended phase, or a
// missing/unparseable startAt/endAt) pass epoch 0 — a past target makes the
// instance INERT on first tick (isPast true → it never arms its interval),
// avoiding an idle 1s timer. showCountdown gates on a real target anyway.
const countdown = useCountdown(adjustedTargetMs ?? 0);

// The caller (PoolaRun) opts in only for the live phase; guard defensively here
// too so a stray `festival` prop never skins a scheduled/ended header.
const festivalActive = computed(() => props.festival && props.phase === 'active');

const title = computed(() => {
  if (festivalActive.value) return t('poola.festival.title');
  if (props.phase === 'scheduled') return t('poola.buy.comingSoon.header');
  if (props.phase === 'ended') return t('poola.buy.ended.header');
  return t('poola.buy.live.title');
});

const countdownLabel = computed(() => {
  if (festivalActive.value) return t('poola.festival.countdown.header');
  return props.phase === 'scheduled'
    ? t('poola.buy.comingSoon.startsIn')
    : t('poola.buy.live.endsIn');
});

const showCountdown = computed(
  () => props.phase !== 'ended' && adjustedTargetMs != null && !countdown.isPast,
);

// Day counter needs BOTH ends of the run window — captured once like
// adjustedTargetMs (this instance's phase/deal are stable for its lifetime).
const adjustedStartMs = (() => {
  const raw = toMs(props.deal?.startAt);
  return raw == null ? null : raw - props.serverOffset;
})();

const dayCounter = computed(() => {
  if (!festivalActive.value) return null;
  // Prefer the server-authoritative day index/count from meta.festival (the BE
  // computes them on Africa/Cairo calendar days + clamps to [1, dayCount], so the
  // chip never disagrees with the admin or other members the way client-side UTC
  // day math can). Both are null for an open-ended run window — fall through to
  // the client derivation then (and for any contract predating meta.festival).
  const di = props.festivalMeta?.dayIndex;
  const dc = props.festivalMeta?.dayCount;
  if (di != null && dc != null) return { day: di, total: dc };
  // Fallback: derive from the run's own startAt/endAt window. Re-derive once per
  // second by riding useCountdown's existing interval — no extra timer for a skin
  // that only needs to change at day boundaries.
  const _recomputeOnTick = countdown.seconds;
  return computeFestivalDayCounter({
    startAtMs: adjustedStartMs,
    endAtMs: adjustedTargetMs,
    nowMs: Date.now(),
  });
});

const festivalUrgency = computed(() => {
  if (!festivalActive.value) return 'none';
  const _recomputeOnTick = countdown.seconds;
  return computeFestivalUrgency({ endAtMs: adjustedTargetMs, nowMs: Date.now() });
});

const urgencyLabel = computed(() => {
  if (festivalUrgency.value === 'lastHours') return t('poola.festival.countdown.lastHours');
  if (festivalUrgency.value === 'lastDay') return t('poola.festival.countdown.lastDay');
  if (festivalUrgency.value === 'closingSoon') return t('poola.festival.countdown.closingSoon');
  return '';
});

// Live-surface slots (P2-B: thermometer/savings/live-pill) — only wrap them when
// the host actually feeds one, so the placeholder renders nothing today.
const slots = useSlots();
const hasLiveSurfaces = computed(
  () => Boolean(slots.thermometer) || Boolean(slots.savings) || Boolean(slots['live-pill']),
);

// Run cap headroom (cap − used) from the backend; null = uncapped (chip hidden).
const runRemainingCents = computed(() => props.deal?.myPoolshareRemainingCents ?? null);
// What the member can actually spend = min(cap headroom, wallet balance). Clamp
// only when we have a finite balance; if the wallet read is still in flight we
// fall back to the raw headroom rather than flashing a 0.
const budgetCents = computed(() => {
  const remaining = runRemainingCents.value;
  if (remaining == null) return null;
  const balance = props.poolshareBalanceCents;
  return Number.isFinite(balance) ? Math.min(remaining, Math.max(0, balance)) : remaining;
});
const showBudget = computed(
  () => !props.hideBudget && props.phase !== 'ended' && budgetCents.value != null,
);

function pad(n) {
  return String(n).padStart(2, '0');
}
</script>

<style scoped>
.poola-run-header {
  padding: 24px 20px 12px;
  text-align: center;
}

.poola-run-header__title {
  color: var(--poolit-primary-800);
  font-size: var(--poolit-font-size-h3);
  font-weight: var(--poolit-font-weight-bold);
  margin: 0 0 14px;
  line-height: var(--poolit-line-height-h3);
}

.poola-run-header__countdown {
  margin: 0 auto 12px;
}

.poola-run-header__countdown-label {
  display: block;
  color: var(--poolit-text-secondary);
  font-size: var(--poolit-font-size-caption);
  font-weight: var(--poolit-font-weight-semibold);
  letter-spacing: 0.04em;
  margin-bottom: 10px;
}

.poola-run-header__cells {
  display: flex;
  justify-content: center;
  gap: 8px;
}

.poola-run-header__cell {
  flex: 1;
  max-width: 64px;
  background: var(--poolit-primary-900);
  border-radius: var(--poolit-radius-md);
  padding: 10px 4px;
}

.poola-run-header__value {
  display: block;
  font-size: 1.5rem;
  font-weight: var(--poolit-font-weight-bold);
  color: #fff;
  line-height: 1;
  letter-spacing: 0.02em;
  font-variant-numeric: tabular-nums;
}

.poola-run-header__unit {
  display: block;
  font-size: var(--poolit-font-size-small);
  color: var(--poolit-primary-300);
  margin-top: 5px;
}

.poola-run-header__budget {
  display: inline-block;
  background: var(--poolit-primary-100);
  color: var(--poolit-primary-700);
  border-radius: var(--poolit-radius-full);
  padding: 5px 14px;
  font-size: var(--poolit-font-size-caption);
  font-weight: var(--poolit-font-weight-semibold);
}

/* ── «موسم البولة» festival skin (P2-A) — a time-boxed treatment for the live
     run window only. Motif: a restrained khayamiya-style geometric lattice + a
     bunting strip + jewel-tone/gold accents on existing brand tokens. Motion is
     transform/opacity only (composite-only) and fully gated behind
     prefers-reduced-motion below. ── */
.poola-run-header[data-festival='true'] {
  position: relative;
  margin: 0 12px;
  padding-top: 34px;
  border-radius: var(--poolit-radius-lg);
  overflow: hidden;
  background:
    repeating-linear-gradient(
      45deg,
      rgb(251 191 36 / 8%) 0,
      rgb(251 191 36 / 8%) 1px,
      transparent 1px,
      transparent 18px
    ),
    repeating-linear-gradient(
      -45deg,
      rgb(255 255 255 / 6%) 0,
      rgb(255 255 255 / 6%) 1px,
      transparent 1px,
      transparent 18px
    ),
    linear-gradient(180deg, var(--poolit-primary-900) 0%, var(--poolit-primary-800) 100%);
}

/* Bunting strip — alternating gold/lilac triangular flags along the top edge. */
.poola-run-header[data-festival='true']::before {
  content: '';
  position: absolute;
  inset-inline: 0;
  top: 0;
  height: 14px;
  background-image:
    linear-gradient(135deg, transparent 50%, var(--poolshare-rate-gold-accent) 50%),
    linear-gradient(45deg, var(--poolit-primary-300) 50%, transparent 50%);
  background-size:
    20px 14px,
    20px 14px;
  background-position:
    0 0,
    10px 0;
  background-repeat: repeat-x;
  opacity: 0.85;
}

.poola-run-header[data-festival='true'] .poola-run-header__title {
  position: relative;
  z-index: 1;
  color: #fff;
}

.poola-run-header[data-festival='true'] .poola-run-header__countdown-label {
  color: var(--poolit-primary-200);
}

.poola-run-header__day-counter {
  position: relative;
  z-index: 1;
  display: inline-block;
  margin: 0 0 12px;
  padding: 4px 14px;
  border-radius: var(--poolit-radius-full);
  background: var(--poolshare-rate-gold-bg);
  color: var(--poolshare-rate-gold-fg);
  font-size: var(--poolit-font-size-caption);
  font-weight: var(--poolit-font-weight-bold);
  letter-spacing: 0.02em;
  font-variant-numeric: tabular-nums;
}

.poola-run-header__urgency-badge {
  position: relative;
  z-index: 1;
  display: inline-block;
  margin-top: 10px;
  padding: 4px 12px;
  border-radius: var(--poolit-radius-full);
  background: var(--poolit-primary-100);
  color: var(--poolit-primary-700);
  font-size: var(--poolit-font-size-caption);
  font-weight: var(--poolit-font-weight-semibold);
}

/* Escalating urgency color — closingSoon (calm gold) → lastDay (warm) → lastHours (hot). */
.poola-run-header[data-urgency='closingSoon'] .poola-run-header__urgency-badge {
  background: var(--poolshare-rate-gold-bg);
  color: var(--poolshare-rate-gold-fg);
}

.poola-run-header[data-urgency='lastDay'] .poola-run-header__urgency-badge {
  background: var(--poolit-warning-100);
  color: var(--poolit-warning-700);
}

.poola-run-header[data-urgency='lastHours'] .poola-run-header__urgency-badge {
  background: var(--poolit-error-100);
  color: var(--poolit-error-dark);
}

.poola-run-header__finale {
  position: relative;
  z-index: 1;
  margin: 12px 0 0;
  color: #fff;
  font-size: var(--poolit-font-size-small);
  font-weight: var(--poolit-font-weight-semibold);
}

/* "Runs hot" finale skin — a warmer wash once the last-hours window opens; the
   finale banner breathes gently. transform/opacity only, never box-shadow or
   color loops (perf: composite-only). */
.poola-run-header[data-urgency='lastHours'] {
  background:
    repeating-linear-gradient(
      45deg,
      rgb(251 191 36 / 12%) 0,
      rgb(251 191 36 / 12%) 1px,
      transparent 1px,
      transparent 18px
    ),
    linear-gradient(180deg, #5a1a1a 0%, var(--poolit-primary-900) 100%);
}

.poola-run-header[data-urgency='lastHours'] .poola-run-header__finale {
  animation: poola-festival-finale-pulse 2.4s ease-in-out infinite;
}

@keyframes poola-festival-finale-pulse {
  0%,
  100% {
    opacity: 0.75;
    transform: scale(1);
  }

  50% {
    opacity: 1;
    transform: scale(1.03);
  }
}

@media (prefers-reduced-motion: reduce) {
  .poola-run-header[data-urgency='lastHours'] .poola-run-header__finale {
    animation: none;
  }
}

.poola-run-header__live-surfaces {
  position: relative;
  z-index: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  margin-top: 12px;
}
</style>
